<template>
  <v-form v-model="isFormValid" class="aveine-wine-of-the-month-form">
    <v-container v-if="dataLoaded">

      <!-- Selecteur de MOIS et non de jour : la colonne est ramenee au premier
           du mois par CRUD-Wine, proposer un jour laisserait croire qu'il
           compte. -->
      <v-menu v-model="monthMenu" :close-on-content-click="false" max-width="290">
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="month"
            :label="$t('intranet.homeContent.wineOfTheMonth.month')"
            :rules="[v => !!v || $t('intranet.homeContent.validation.monthRequired')]"
            :error-messages="monthTaken ? [monthTakenMessage] : []"
            :loading="checkingMonth"
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker v-model="month" type="month" @input="monthMenu = false"></v-date-picker>
      </v-menu>

      <!-- Le mois occupe se dit ici ET sous le champ : le bandeau explique quoi
           faire, le message sous le champ designe le coupable. -->
      <v-alert v-if="monthTaken" type="error" dense text class="mt-2">
        {{ monthTakenMessage }}
        <router-link
          v-if="conflict"
          :to="`/authenticated/wines-of-the-month/update/${conflict.id}`"
        >{{ $t('intranet.homeContent.wineOfTheMonth.openExisting') }}</router-link>
      </v-alert>

      <AveineWinePicker
        v-model="wine"
        :label="$t('intranet.homeContent.wineOfTheMonth.wine')"
        :required="true"
        :APIbaseURL="APIbaseURL"
      />

      <!-- La photo officielle du vin choisi, modifiable sans passer par sa
           fiche : la redaction la change chaque mois. Elle appartient au VIN,
           pas au vin du mois, et part des le choix du fichier, enregistrement
           du formulaire ou non. `key` : le composant ne lit son etat qu'a la
           creation, changer de vin doit donc le recreer. -->
      <AveineBottleImage
        v-if="wine"
        :key="wine.id"
        resource="wines"
        :id="wine.id"
        :APIbaseURL="APIbaseURL"
      />

      <AveineTranslatedField
        :value="wineOfTheMonth.description"
        :label="$t('intranet.homeContent.wineOfTheMonth.description')"
      />

      <v-divider class="my-9"></v-divider>

      <div class="d-flex align-center mb-4">
        <h3>{{ $t('intranet.homeContent.wineOfTheMonth.tags') }}</h3>
        <v-spacer></v-spacer>
        <v-btn depressed color="primary" @click="addTag()">
          {{ $t('intranet.homeContent.button.addTag') }}
        </v-btn>
      </div>
      <p class="text--secondary">{{ $t('intranet.homeContent.wineOfTheMonth.tagsHint') }}</p>

      <v-row v-for="(tag, index) in tags" :key="tag.id || index" class="align-center">
        <v-col>
          <AveineTranslatedField
            :value="tag.label"
            :label="$t('intranet.homeContent.wineOfTheMonth.tagLabel', { position: index + 1 })"
          />
        </v-col>
        <v-col cols="1" class="d-flex justify-center">
          <v-btn icon color="error" @click="removeTag(index)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-alert v-if="tagsError" type="warning" dense text>{{ tagsError }}</v-alert>

      <v-divider class="my-9"></v-divider>

      <v-switch
        v-model="wineOfTheMonth.published"
        :label="$t('intranet.homeContent.attributes.published')"
        :hint="$t('intranet.homeContent.wineOfTheMonth.publishedHint')"
        persistent-hint
      ></v-switch>

      <div class="d-flex justify-center mt-9">
        <v-btn
          class="md-raised"
          :disabled="!isFormValid || !!tagsError || monthTaken || checkingMonth"
          depressed
          color="primary"
          @click="submit()"
        >
          {{ $t('intranet.referential.button.submit') }}
        </v-btn>
      </div>
    </v-container>

    <div v-else class="d-flex justify-center align-center mt-16">
      <v-progress-circular indeterminate :size="100" :width="10" color="secondary"></v-progress-circular>
    </div>
  </v-form>
</template>

<script>
import { errorHandler } from 'aveine-toolbox'
import AveineBottleImage from '@/components/AveineBottleImage.vue'
import AveineTranslatedField from '@/components/AveineTranslatedField.vue'
import AveineWinePicker from '@/components/AveineWinePicker.vue'
import WineOfTheMonth from '@/store/models/wine-of-the-month'
import WineOfTheMonthTag from '@/store/models/wine-of-the-month-tag'

/**
 * Formulaire du « Vin du mois » (REDMINE-12962).
 *
 * Un seul choix par mois calendaire, garanti par un index unique partiel cote
 * base. Ce formulaire ne verifie donc PAS l'unicite avant d'envoyer : un
 * controle « existe-t-il deja ? » ouvrirait une course entre deux redacteurs et
 * donnerait une fausse impression de securite. Un doublon remonte en erreur
 * d'ecriture, traduite par errorHandler.
 */
export default {
  name: 'AveineWineOfTheMonthForm',
  components: { AveineBottleImage, AveineTranslatedField, AveineWinePicker },
  props: {
    wineOfTheMonth: { type: Object, required: true },
    APIbaseURL: String
  },
  data: () => ({
    isFormValid: false,
    dataLoaded: false,
    monthMenu: false,
    // « 2026-09 », la forme que rend v-date-picker en mode mois.
    month: null,
    wine: null,
    tags: [],
    removed: [],
    // Le vin du mois deja en place sur le mois choisi, s'il y en a un et qu'il
    // n'est pas celui qu'on edite.
    conflict: null,
    checkingMonth: false
  }),
  computed: {
    tagsError () {
      return this.tags.some(tag => !tag.label.fr || !tag.label.en)
        ? this.$t('intranet.homeContent.validation.tagTranslations')
        : null
    },
    monthTaken () {
      return !!this.conflict
    },
    monthTakenMessage () {
      if (!this.conflict) return ''
      return this.$t('intranet.homeContent.validation.monthTaken', {
        month: this.formatMonth(this.month),
        wine: this.conflict.label
      })
    }
  },
  watch: {
    // Le controle part des que le mois change, sans attendre l'enregistrement :
    // decouvrir le conflit apres avoir redige la description serait cruel.
    month () {
      this.checkMonth()
    }
  },
  created () {
    if (!this.wineOfTheMonth.description) this.$set(this.wineOfTheMonth, 'description', {})

    // L'API rend un horodatage ISO complet ; le selecteur veut « AAAA-MM ».
    this.month = this.wineOfTheMonth.month ? this.wineOfTheMonth.month.slice(0, 7) : null

    // Un vin n'a pas de nom : `cuvee` et le domaine vivent sur son TEMPLATE,
    // seul le millesime lui appartient. Meme recomposition que dans
    // AveineWinePicker, d'ou le vin selectionne s'affichera a l'identique.
    if (this.wineOfTheMonth.wine) {
      const wine = this.wineOfTheMonth.wine
      const template = wine.template || {}
      const domain = template.domain || {}
      this.wine = {
        id: wine.id,
        label: [domain.name, template.cuvee, wine.vintage].filter(Boolean).join(' ') ||
          this.$t('intranet.homeContent.picker.unnamedWine')
      }
    }

    this.tags = (this.wineOfTheMonth.tags || [])
      .slice()
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map(tag => ({ ...tag, label: { ...tag.label } }))

    this.dataLoaded = true
  },
  methods: {
    /** « septembre 2026 » a partir de « 2026-09 », sans passer par un fuseau. */
    formatMonth (month) {
      if (!month) return ''
      const [year, monthNumber] = month.split('-')
      return new Date(Number(year), Number(monthNumber) - 1, 1, 12)
        .toLocaleDateString(this.$i18n.locale, { month: 'long', year: 'numeric' })
    },

    /**
     * Cherche un vin du mois deja pose sur le mois choisi.
     *
     * Ce controle est une COMMODITE, pas la garantie : entre cette lecture et
     * l'enregistrement, un autre redacteur peut prendre le mois. La garantie
     * reste l'index unique partiel `wines_of_the_month_unique_month`, et
     * `submit` rattrape le cas ou la course a ete perdue.
     *
     * Intervalle semi-ouvert plutot qu'une egalite sur l'horodatage : le mois
     * est stocke au premier jour a minuit, mais faire reposer la requete sur
     * une correspondance de chaine au format pres serait fragile.
     */
    async checkMonth () {
      this.conflict = null
      if (!this.month) return

      const [year, monthNumber] = this.month.split('-').map(Number)
      const start = new Date(Date.UTC(year, monthNumber - 1, 1)).toISOString().replace(/\.\d{3}Z$/, 'Z')
      const next = new Date(Date.UTC(year, monthNumber, 1)).toISOString().replace(/\.\d{3}Z$/, 'Z')

      this.checkingMonth = true
      try {
        const response = await WineOfTheMonth.api().get(
          `wines-of-the-month?lang=${this.$i18n.locale}`,
          {
            baseURL: this.APIbaseURL,
            save: false,
            params: {
              include: 'wine.template.domain',
              // `;` est le ET de FIQL.
              filter: `month=ge=${start};month=lt=${next}`,
              'page[size]': 2
            }
          }
        )

        const found = (response.response.data.data || [])
          // En edition, se croiser soi-meme n'est pas un conflit.
          .filter(entry => entry.id !== this.wineOfTheMonth.id)

        if (found.length) {
          this.conflict = {
            id: found[0].id,
            label: this.describeIncludedWine(found[0], response.response.data.included || [])
          }
        }
      } catch (errors) {
        // Un echec de ce controle ne doit pas bloquer la saisie : la base
        // refusera de toute facon un doublon. On laisse donc passer, en silence.
        this.conflict = null
      } finally {
        this.checkingMonth = false
      }
    },

    /** Recompose « domaine cuvee millesime » depuis le document JSON:API. */
    describeIncludedWine (entry, included) {
      const wineId = entry.relationships && entry.relationships.wine
        ? entry.relationships.wine.data.id
        : null
      const wine = included.find(r => r.type === 'wines' && r.id === wineId)
      if (!wine) return this.$t('intranet.homeContent.picker.unnamedWine')

      const templateId = wine.relationships && wine.relationships.template
        ? wine.relationships.template.data.id
        : null
      const template = included.find(r => r.type === 'wine-templates' && r.id === templateId)
      const domainId = template && template.relationships && template.relationships.domain
        ? template.relationships.domain.data.id
        : null
      const domain = included.find(r => r.type === 'domains' && r.id === domainId)

      const domainName = domain && domain.attributes.name
      const cuvee = template && template.attributes.cuvee

      return [
        domainName,
        // Beaucoup de domaines nomment leur cuvee comme eux-memes : on evite
        // « Château Margaux Château Margaux 1984 ».
        cuvee === domainName ? null : cuvee,
        wine.attributes.vintage
      ].filter(Boolean).join(' ') || this.$t('intranet.homeContent.picker.unnamedWine')
    },

    addTag () {
      this.tags.push({ id: null, label: {}, position: null })
    },

    removeTag (index) {
      const [tag] = this.tags.splice(index, 1)
      if (tag.id) this.removed.push(tag)
    },

    async submit () {
      // Dernier controle avant d'ecrire : le mois a pu etre pris depuis que le
      // formulaire est ouvert. On ne peut pas fermer la course, seulement la
      // reduire — d'ou le rattrapage sur l'erreur d'ecriture plus bas.
      await this.checkMonth()
      if (this.monthTaken) {
        this.$toastr.e(this.monthTakenMessage)
        return
      }

      try {
        this.dataLoaded = false

        // Minuit UTC au premier du mois : la forme exacte que la base exige
        // (CHECK date_trunc). CRUD-Wine renormalise de toute facon, mais
        // envoyer la valeur juste evite un aller-retour inutile.
        this.wineOfTheMonth.month = `${this.month}-01T00:00:00Z`
        this.wineOfTheMonth.wine_id = this.wine.id
        // `$entity_name` est indispensable : le dataTree en tire le type
        // JSON:API du lien. Un objet nu donnerait `{"type": null}`, et
        // `JAPI::ResourceLinkage#type` n'est pas nilable cote creddy.
        this.wineOfTheMonth.wine = { $entity_name: 'wines', id: this.wine.id }

        const url = this.wineOfTheMonth.$isNew
          ? `wines-of-the-month?lang=${this.$i18n.locale}`
          : `wines-of-the-month/${this.wineOfTheMonth.id}?lang=${this.$i18n.locale}`

        const response = this.wineOfTheMonth.$isNew
          ? await WineOfTheMonth.api().post(url, this.wineOfTheMonth, { baseURL: this.APIbaseURL })
          : await WineOfTheMonth.api().patch(url, this.wineOfTheMonth, { baseURL: this.APIbaseURL })

        const id = this.wineOfTheMonth.$isNew
          ? response.entities.wines_of_the_month[0].id
          : this.wineOfTheMonth.id

        await this.saveTags(id)

        this.$toastr.s(this.$t('intranet.referential.title.saveConfirmation'))
        this.$router.push('/authenticated/wines-of-the-month/list')
      } catch (errors) {
        this.dataLoaded = true

        // Course perdue : quelqu'un a pris le mois entre notre verification et
        // notre ecriture. La base a fait son office ; on traduit sa violation
        // d'index en phrase lisible plutot que de laisser passer un message SQL.
        if (this.isDuplicateMonth(errors)) {
          await this.checkMonth()
          this.$toastr.e(this.monthTakenMessage || this.$t('intranet.homeContent.validation.monthTakenRace'))
          return
        }

        errorHandler(errors, this)
      }
    },

    /** Reconnait la violation de `wines_of_the_month_unique_month`. */
    isDuplicateMonth (errors) {
      const body = errors && errors.response && errors.response.data
      if (!body || !Array.isArray(body.errors)) return false
      return body.errors.some(e =>
        (e.detail || '').includes('wines_of_the_month_unique_month')
      )
    },

    /**
     * Aligne les etiquettes sur ce qu'affiche le formulaire. Aucune contrainte
     * d'unicite ici, contrairement aux reponses d'un quizz : la suppression et
     * les ecritures peuvent s'enchainer sans precaution particuliere.
     */
    async saveTags (wineOfTheMonthId) {
      for (const tag of this.removed) {
        await WineOfTheMonthTag.api().delete(
          `wine-of-the-month-tags/${tag.id}`,
          { baseURL: this.APIbaseURL, save: false }
        )
      }
      this.removed = []

      for (const [index, tag] of this.tags.entries()) {
        const payload = { label: tag.label, position: index + 1 }

        if (tag.id) {
          // `id` parce que le serialiseur fait `data.id.charAt()` sans garde,
          // et `$entity_name` parce que c'est de la que vient le type JSON:API :
          // sans lui le corps part avec `"type": null` et creddy refuse en 400.
          await WineOfTheMonthTag.api().patch(
            `wine-of-the-month-tags/${tag.id}`,
            { $entity_name: WineOfTheMonthTag.entity, id: tag.id, ...payload },
            { baseURL: this.APIbaseURL, save: false }
          )
        } else {
          const draft = await WineOfTheMonthTag.new()
          Object.assign(draft, payload)
          draft.wine_of_the_month_id = wineOfTheMonthId
          draft.wine_of_the_month = {
            $entity_name: 'wines_of_the_month',
            id: wineOfTheMonthId
          }
          draft.$isNew = true
          await WineOfTheMonthTag.api().post(
            'wine-of-the-month-tags',
            draft,
            { baseURL: this.APIbaseURL, save: false }
          )
        }
      }
    }
  }
}
</script>
